// src/App.jsx
import React, { useState, useEffect } from "react";
import JeopardyBoard from "./components/JeopardyBoard";
import JeopardyTeam from "./components/JeopardyTeam";
import CoinFlipModal from "./components/CoinFlipModal";
import QuestionModal from "./components/QuestionModal";
import TeamSelectionModal from "./components/TeamSelectionModal";
import "./styles.css";

function App() {
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isCoinFlipOpen, setIsCoinFlipOpen] = useState(false);
  const [isTeamSelectionOpen, setIsTeamSelectionOpen] = useState(false);
  const [tempSelectedQuestion, setTempSelectedQuestion] = useState(null);
  const [answeringTeamId, setAnsweringTeamId] = useState(null);
  
  // Initialize teams from localStorage or use default values
  const [teams, setTeams] = useState(() => {
    const savedTeams = localStorage.getItem('jeopardy-teams');
    if (savedTeams) {
      return JSON.parse(savedTeams);
    }
    return [
      { id: 1, name: "Tým A", points: 0, helpers: "111" },
      { id: 2, name: "Tým B", points: 0, helpers: "111" },
      { id: 3, name: "Tým C", points: 0, helpers: "111" },
    ];
  });
  
  // Initialize used cells from localStorage
  const [usedCells, setUsedCells] = useState(() => {
    const savedCells = localStorage.getItem('jeopardy-used-cells');
    if (savedCells) {
      return JSON.parse(savedCells);
    }
    return {};
  });
  
  // Save teams to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('jeopardy-teams', JSON.stringify(teams));
  }, [teams]);
  
  // Save used cells to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('jeopardy-used-cells', JSON.stringify(usedCells));
  }, [usedCells]);
  
  // Add or subtract points from a team
  const updateTeamPoints = (teamId, points) => {
    setTeams(prevTeams => 
      prevTeams.map(team => 
        team.id === teamId ? { ...team, points: team.points + points } : team
      )
    );
  };
  
  // Update team name
  const updateTeamName = (teamId, newName) => {
    setTeams(prevTeams => 
      prevTeams.map(team => 
        team.id === teamId ? { ...team, name: newName } : team
      )
    );
  };
  
  // Mark helper as used
  const useHelper = (teamId, helperIndex) => {
    setTeams(prevTeams => 
      prevTeams.map(team => {
        if (team.id === teamId) {
          const helperArray = team.helpers.split("");
          if (helperArray[helperIndex] === "1") {
            helperArray[helperIndex] = "0";
            return { ...team, helpers: helperArray.join("") };
          }
        }
        return team;
      })
    );
  };

  // Update used cells
  const handleCellUsed = (categoryIdx, questionIdx) => {
    const key = `${categoryIdx}-${questionIdx}`;
    setUsedCells(prev => ({ ...prev, [key]: true }));
  };

  // Handle question selection - now opens team selection first
  const handleQuestionSelect = (q) => {
    setTempSelectedQuestion(q);
    setIsTeamSelectionOpen(true);
  };

  // Handle team selection for answering
  const handleTeamSelect = (teamId) => {
    setAnsweringTeamId(teamId);
    setSelectedQuestion(tempSelectedQuestion);
    setIsTeamSelectionOpen(false);
  };

  // Reset game state
  const resetGame = () => {
    if (window.confirm("Are you sure you want to reset the game? This will clear all scores and restore all questions.")) {
      localStorage.removeItem('jeopardy-used-cells');
      localStorage.removeItem('jeopardy-teams');
      
      setUsedCells({});
      setTeams([
        { id: 1, name: "Team A", points: 0, helpers: "111" },
        { id: 2, name: "Team B", points: 0, helpers: "111" },
        { id: 3, name: "Team C", points: 0, helpers: "111" },
      ]);
    }
  };

  const handleQuestionClosed = () => {
    setSelectedQuestion(null);
    setAnsweringTeamId(null);
  };

  return (
    <div className="app-container">
      {/* Header with Reset Button 
      <div className="header-container">
        <button onClick={resetGame} className="btn reset-btn">Reset Game</button>
      </div>
      */}
      {/* Jeopardy Board */}
      <JeopardyBoard 
        onQuestionSelect={handleQuestionSelect} 
        usedCells={usedCells}
        onCellUsed={handleCellUsed}
      />

      {/* Teams row */}
      <div className="teams">
        {teams.map(team => (
          <JeopardyTeam
            key={team.id}
            id={team.id}
            name={team.name}
            points={team.points}
            helpers={team.helpers}
            onCoinFlip={() => setIsCoinFlipOpen(true)}
            onUpdatePoints={updateTeamPoints}
            onUpdateName={updateTeamName}
            onUseHelper={useHelper}
          />
        ))}
      </div>

      {/* Team Selection Modal */}
      <TeamSelectionModal
        isOpen={isTeamSelectionOpen}
        onClose={() => setIsTeamSelectionOpen(false)}
        teams={teams}
        onTeamSelect={handleTeamSelect}
      />

      {/* Question Modal */}
      <QuestionModal
        question={selectedQuestion}
        answeringTeamId={answeringTeamId}
        onClose={handleQuestionClosed}
        teams={teams}
        onUseHelper={useHelper}
        onUpdatePoints={updateTeamPoints}
      />

      {/* Coin Flip Modal */}
      <CoinFlipModal
        isOpen={isCoinFlipOpen}
        onClose={() => setIsCoinFlipOpen(false)}
      />
    </div>
  );
}

export default App;